// FILE: src/components/TargetedTestConfig.js
//
// Configuration screen for the user-configured "Targeted Test" mode.
// Shown after the user clicks the Targeted Test tile in ModeSelector and
// before the actual session is built.
//
// ── Behaviour ─────────────────────────────────────────────────────────────────
//
//   1. Pulls the live question pool for the selected exam from the shared
//      questionStores (falling back to bundled JSON via loadQuestions).
//   2. Calls extractMetadataOptions(pool, examId) to discover which metadata fields
//      have at least two distinct values.  Only those fields are surfaced —
//      so PMP, CPMAI, and PMI-ACP each see what they actually populate, with
//      no exam-specific code.
//   3. Lets the user toggle filter values (checkboxes — multi-select) and
//      pick a question count (radios — single-select per the project rule
//      that single-select questions use radios).
//   4. Live match count is shown as the user toggles filters.
//   5. Validates: at least one filter value must be selected before Start.
//   6. If the matching pool is smaller than the requested count, the warning
//      banner offers two clear actions: "Start with N available" or
//      "Adjust filters" — never silently fails.
//
// ── Props ─────────────────────────────────────────────────────────────────────
//
//   examId   {string}     'pmp' | 'cpmai' | 'pmi-acp'
//   pool     {object[]}   live question pool for the exam (active questions
//                         after quarantine has been applied by the caller)
//   onStart  {function}   ({ filters, count }) => void
//   onBack   {function}   () => void   — return to ModeSelector

import React, { useEffect, useMemo, useState } from 'react';
import { getExamConfig } from '../../config/exams';
import { extractMetadataOptions, filterByMetadata } from '../../services/questionService';
import ScreenHeader from '../shared/ScreenHeader';
import SessionStartStatus from '../shared/SessionStartStatus';

const S = {
  wrapper: {
    maxWidth: '720px',
    margin: '40px auto',
    padding: '24px',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    color: 'var(--ink)',
  },
  section: {
    background: '#fff',
    border: '1px solid var(--line)',
    borderRadius: '8px',
    padding: '16px 20px',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: '700',
    margin: '0 0 12px',
    color: 'var(--ink)',
  },
  fieldGroup: {
    marginBottom: '14px',
  },
  fieldLabel: {
    fontSize: '0.85rem',
    fontWeight: '600',
    color: 'var(--muted)',
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  optionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px 14px',
  },
  optionLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.9rem',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    background: 'var(--bg)',
    border: '1px solid var(--line)',
  },
  countRow: {
    display: 'flex',
    gap: '14px',
    flexWrap: 'wrap',
  },
  matchBanner: (isShortfall) => ({
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '0.9rem',
    marginBottom: '16px',
    background: isShortfall ? 'var(--warn-soft)' : 'var(--good-soft)',
    border: `1px solid ${isShortfall ? 'var(--warn)' : 'var(--good)'}`,
    color: isShortfall ? 'var(--warn)' : 'var(--good)',
  }),
  errorBanner: {
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '0.9rem',
    marginBottom: '16px',
    background: 'var(--bad-soft)',
    border: '1px solid var(--bad)',
    color: 'var(--bad)',
  },
  actionRow: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '8px',
    flexWrap: 'wrap',
  },
  btnPrimary: {
    padding: '10px 20px',
    background: 'var(--brand)',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.95rem',
    fontWeight: '700',
    cursor: 'pointer',
  },
  btnPrimaryDisabled: {
    padding: '10px 20px',
    background: 'var(--line-strong)',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.95rem',
    fontWeight: '700',
    cursor: 'not-allowed',
  },
  btnSecondary: {
    padding: '10px 20px',
    background: 'transparent',
    border: '1px solid var(--line-strong)',
    borderRadius: '6px',
    fontSize: '0.95rem',
    color: 'var(--ink)',
    cursor: 'pointer',
  },
  emptyState: {
    fontStyle: 'italic',
    color: 'var(--muted)',
    fontSize: '0.9rem',
    padding: '12px 0',
  },
};

export default function TargetedTestConfig({ examId, pool, onStart, onBack, sessionStart, onRetrySessionStart }) {
  // PR 2A: disable the Start actions while the cert bank loads for this start.
  const starting = !!sessionStart && sessionStart.status === 'loading';
  const config = getExamConfig(examId);
  const allowedCounts = config?.targetedTest?.allowedCounts || [10, 15, 20, 25];
  const defaultCount  = config?.targetedTest?.defaultCount || 15;

  // Derive available metadata groups once from the (active) pool, then apply
  // per-exam display overrides so PMP shows "Phases" with the standard PMI
  // process-group naming (Executing, not Execution).  Underlying values are
  // unchanged — the filter payload still uses the raw category string.
  //
  // valueAllowlist (optional, per-exam) restricts the surfaced options to a
  // canonical set.  This protects against dirty data — e.g. PMP's "Phases"
  // group should only ever show the five PMI process groups, never a stray
  // knowledge-area like "Earned Value" that may exist on a staging bank.
  const groups = useMemo(() => {
    const raw = extractMetadataOptions(pool || [], examId);
    const overrides = config?.metadataDisplay || {};
    return raw.map((g) => {
      const o = overrides[g.key];
      if (!o) return g;
      const allowed = Array.isArray(o.valueAllowlist) && o.valueAllowlist.length > 0
        ? new Set(o.valueAllowlist)
        : null;
      const filteredValues = allowed
        ? g.values.filter((v) => allowed.has(v))
        : g.values;
      return {
        ...g,
        label: o.groupLabel || g.label,
        valueLabels: o.valueLabels || {},
        values: filteredValues,
      };
    }).filter((g) => g.values.length > 0); // drop empty groups
  }, [pool, config, examId]);

  // Selection state: { fieldKey: Set<string> }.  Backed by Sets for O(1)
  // toggle but exposed as plain arrays to consumers.
  const [selected, setSelected] = useState(() => {
    const init = {};
    for (const g of groups) init[g.key] = new Set();
    return init;
  });
  const [count, setCount] = useState(defaultCount);

  // Build the plain-object filters payload the questionService expects.
  const filtersPayload = useMemo(() => {
    const out = {};
    for (const g of groups) {
      const set = selected[g.key];
      if (set && set.size > 0) out[g.key] = [...set];
    }
    return out;
  }, [selected, groups]);

  const hasAnyFilter = Object.keys(filtersPayload).length > 0;

  // Live match count — recomputed every keystroke/toggle.  Cheap on a few
  // thousand questions; if pools grow to 100k we can debounce.
  const matched = useMemo(
    () => (hasAnyFilter ? filterByMetadata(pool || [], filtersPayload) : []),
    [pool, filtersPayload, hasAnyFilter]
  );
  const matchCount  = matched.length;
  const isShortfall = hasAnyFilter && matchCount < count;
  const noMatches   = hasAnyFilter && matchCount === 0;

  function toggleValue(fieldKey, value) {
    setSelected((prev) => {
      const next = { ...prev };
      const set = new Set(prev[fieldKey] || []);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      next[fieldKey] = set;
      return next;
    });
  }

  function handleStart(useAvailable = false) {
    if (!hasAnyFilter) return;
    if (matchCount === 0) return;
    const finalCount = useAvailable ? matchCount : Math.min(count, matchCount);
    onStart({ filters: filtersPayload, count: finalCount });
  }

  // Watch viewport so we can use a smaller avatar on narrow screens.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(max-width: 560px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 560px)');
    const onChange = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  return (
    <div style={S.wrapper} data-testid="targeted-test-config">
      {/* ── Standard topmast (ScreenHeader banner) ── */}
      <ScreenHeader
        variant="banner"
        coachPose="study"
        coachHeightPx={isMobile ? 120 : 170}
        eyebrow="Session Setup"
        title="Targeted Test"
        subtitle={`Pick the areas you want to focus on, choose how many questions you'd like, and we'll build a session from ${config?.displayName || 'your exam'} that matches.`}
      />

      {/* ── Metadata filter groups ─────────────────────────────────────── */}
      <div style={S.section}>
        <h2 style={S.sectionTitle}>Focus areas</h2>
        {groups.length === 0 && (
          <div style={S.emptyState} data-testid="targeted-test-no-metadata">
            No metadata available to filter on for this exam yet.
          </div>
        )}
        {groups.map((g) => (
          <div key={g.key} style={S.fieldGroup} data-testid={`targeted-test-group-${g.key}`}>
            <div style={S.fieldLabel}>{g.label}</div>
            <div style={S.optionRow}>
              {g.values.map((v) => {
                const isOn = selected[g.key]?.has(v);
                const display = (g.valueLabels && g.valueLabels[v]) || v;
                return (
                  <label key={v} style={S.optionLabel}>
                    <input
                      type="checkbox"
                      data-testid={`targeted-test-option-${g.key}-${v}`}
                      checked={!!isOn}
                      onChange={() => toggleValue(g.key, v)}
                    />
                    {display}
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* ── Question count ─────────────────────────────────────────────── */}
      <div style={S.section}>
        <h2 style={S.sectionTitle}>How many questions?</h2>
        <div style={S.countRow} role="radiogroup" aria-label="Question count">
          {allowedCounts.map((n) => (
            <label key={n} style={S.optionLabel}>
              <input
                type="radio"
                name="targeted-test-count"
                data-testid={`targeted-test-count-${n}`}
                value={n}
                checked={count === n}
                onChange={() => setCount(n)}
              />
              {n}
            </label>
          ))}
        </div>
      </div>

      {/* ── Match-count + warnings ─────────────────────────────────────── */}
      {hasAnyFilter && !noMatches && (
        <div
          data-testid="targeted-test-match-banner"
          style={S.matchBanner(isShortfall)}
        >
          {isShortfall
            ? `Only ${matchCount} matching question${matchCount === 1 ? '' : 's'} available — fewer than your requested ${count}. ` +
              `You can start with the ${matchCount} available, or adjust your filters to widen the pool.`
            : `${matchCount} matching question${matchCount === 1 ? '' : 's'} available.`}
        </div>
      )}

      {noMatches && (
        <div data-testid="targeted-test-no-matches" style={S.errorBanner}>
          No questions match the filters you selected. Pick at least one focus
          area that has questions available.
        </div>
      )}

      {!hasAnyFilter && (
        <div data-testid="targeted-test-no-filters" style={S.errorBanner}>
          Pick at least one focus area to build your targeted test.
        </div>
      )}

      {/* ── Actions ────────────────────────────────────────────────────── */}
      <div style={S.actionRow}>
        <button
          data-testid="targeted-test-back"
          style={S.btnSecondary}
          onClick={onBack}
        >
          ← Back
        </button>

        {isShortfall && (
          <button
            data-testid="targeted-test-start-available"
            style={starting ? S.btnPrimaryDisabled : S.btnSecondary}
            disabled={starting}
            onClick={() => { if (!starting) handleStart(true); }}
          >
            Start with {matchCount} available
          </button>
        )}

        <button
          data-testid="targeted-test-start"
          style={(!hasAnyFilter || noMatches || starting) ? S.btnPrimaryDisabled : S.btnPrimary}
          disabled={!hasAnyFilter || noMatches || starting}
          onClick={() => { if (!starting) handleStart(false); }}
        >
          Start Targeted Test →
        </button>
      </div>

      {sessionStart && (
        <SessionStartStatus
          status={sessionStart.status}
          error={sessionStart.error}
          canRetry={sessionStart.canRetry}
          onRetry={onRetrySessionStart}
        />
      )}
    </div>
  );
}
